import "./CapybaraAvatar.css";
import type { AccessoryState, IdleAnimation } from "../types";
import type { ReactionId } from "../lib/emotionCatalog";
import { useEffect, useState } from "react";
import { ACCESSORY_ART, IDLE_ART, THINKING_ART, getReactionArt } from "../lib/assets";

const IDLE_COPY: Record<IdleAnimation, string> = {
  none: "",
  reading: "Studying quantum computing..."
};

const REACTION_COPY: Record<ReactionId, string> = {
  triumph_wooo: "Hype overload!",
  happy_affirm: "Quanty approves",
  happy_laugh: "Laughing",
  sad_sigh: "Tired of small talk",
  sad_wailing: "Existential drizzle",
  fear_gasp: "That was shocking",
  fear_scream: "Quanty is spooked",
  anger_growl: "Stop the greens",
  anger_shout: "ABSOLUTELY NOT",
  disgust_tongue: "Ew, salad",
  disgust_yuck: "Yuck overload",
  surprise_inhale: "Whoa, unexpected!",
  contempt_tsk: "Tsk...",
  contempt_snort: "Snort of disdain",
  confused: "Come again?",
  sleepy_snore: "Nap mode",
  eating_munch: "Crunch crunch"
};

type Props = {
  reaction: ReactionId | null;
  accessories: AccessoryState;
  idleAnimation: IdleAnimation;
  isThinking?: boolean;
};

export function CapybaraAvatar({ reaction, accessories, idleAnimation, isThinking }: Props) {
  const portrait = getReactionArt(reaction);
  const idleAsset = idleAnimation !== "none" ? IDLE_ART[idleAnimation] : null;

  const [thinkingTimedOut, setThinkingTimedOut] = useState(false);

  useEffect(() => {
    let t: number | undefined;
    if (isThinking) {
      setThinkingTimedOut(false);
      t = window.setTimeout(() => setThinkingTimedOut(true), 15_000);
    } else {
      setThinkingTimedOut(false);
    }
    return () => {
      if (t) {
        window.clearTimeout(t);
      }
    };
  }, [isThinking]);

  const showThinking = Boolean(isThinking && !thinkingTimedOut);
  const displaySrc = showThinking ? THINKING_ART : (idleAsset ?? portrait);

  return (
    <div className="capy-wrapper">
      <div className="capy-avatar" data-disgust={accessories.disgust}>
        <img
          className="capy-portrait"
          src={displaySrc}
          alt={showThinking ? "Thinking" : (idleAsset ? "Reading" : "Quanty reaction")}
          loading="lazy"
        />
        {showThinking ? null : (idleAsset ? (
          <div className="capy-idle-overlay" role="status" aria-live="polite" />
        ) : (
          accessories.finance && (
            <img className="capy-accessory" src={ACCESSORY_ART.finance} alt="Finance accessory" loading="lazy" />
          )
        ))}
      </div>
      <p className="capy-status">
        {showThinking ? "Thinking..." : (idleAsset ? IDLE_COPY[idleAnimation] : reaction ? REACTION_COPY[reaction] : "Quanty is listening.")}
      </p>
    </div>
  );
}
